import { MediaData, NewMessage } from '@gripp/shared-logic';
import { SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { useTypedMedia } from '../media/useTypedMedia';

/** Shared send recovery; each picker reports its own aggregate busy state. */
export const useIssueFooterDraft = (
  onNewMessage: (message: NewMessage) => Promise<void>,
  active = true
) => {
  const { t } = useTranslation();
  const media = useTypedMedia<MediaData>([]);
  const [message, updateMessage] = useState('');
  const [isSending, setSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const text = useRef({ value: '', revision: 0 });
  const currentMedia = useRef(media.typedMedia);
  currentMedia.current = media.typedMedia;
  const uploadingSources = useRef(new Set<string>());
  const sending = useRef(false);
  const mounted = useRef(true);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setMessage = (value: SetStateAction<string>) => {
    if (!mounted.current) return;
    const next =
      typeof value === 'function' ? value(text.current.value) : value;
    text.current = { value: next, revision: text.current.revision + 1 };
    updateMessage(next);
  };

  const handleAdd = (item: MediaData) => {
    if (!mounted.current) return;
    currentMedia.current = [...currentMedia.current, item];
    media.handleAdd(item);
  };
  const handleDelete = (id: string) => {
    if (!mounted.current) return;
    currentMedia.current = currentMedia.current.filter(
      (item) => item.id !== id
    );
    media.handleDelete(id);
  };
  const handleUploadProgress = (id: string, progress: number) => {
    if (!mounted.current) return;
    currentMedia.current = currentMedia.current.map((item) =>
      item.id === id
        ? { ...item, uploadProgress: progress, isUploading: progress !== 1 }
        : item
    );
    media.handleUploadProgress(id, progress);
  };
  const setUploading = (source: string, busy: boolean) => {
    if (!mounted.current) return;
    if (busy) uploadingSources.current.add(source);
    else uploadingSources.current.delete(source);
    setIsUploading(uploadingSources.current.size > 0);
  };
  const reset = () => {
    setMessage('');
    currentMedia.current = [];
    media.reset();
  };
  const hasPendingUpload = () =>
    uploadingSources.current.size > 0 ||
    currentMedia.current.some((item) => item.isUploading);
  const canSend =
    active &&
    !isSending &&
    !isUploading &&
    !media.typedMedia.some((item) => item.isUploading) &&
    (message.length > 0 || media.typedMedia.length > 0);

  const attachmentFailed = () => {
    if (mounted.current)
      Toast.show({ type: 'error', text1: t('issue.footer.attachmentFailed') });
  };

  const send = async (): Promise<boolean> => {
    if (
      !mounted.current ||
      !activeRef.current ||
      sending.current ||
      hasPendingUpload() ||
      (!text.current.value.length && !currentMedia.current.length)
    )
      return false;
    sending.current = true;
    setSending(true);
    const submittedText = text.current;
    const submittedMedia = [...currentMedia.current];
    try {
      await onNewMessage({
        message: submittedText.value,
        media: submittedMedia,
      });
      if (!mounted.current) return false;
      // Revision, rather than equality, protects edits that return to the same text.
      if (text.current.revision === submittedText.revision) setMessage('');
      submittedMedia.forEach((item) => handleDelete(item.id));
      return (
        !text.current.value.length &&
        !currentMedia.current.length &&
        !hasPendingUpload()
      );
    } catch (error) {
      if (mounted.current)
        Toast.show({ type: 'error', text1: t('chat.composer.postFailed') });
      return false;
    } finally {
      sending.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return {
    message,
    setMessage,
    messageMedia: media.typedMedia,
    canSend,
    isSending,
    isUploading,
    setUploading,
    handleAdd,
    handleDelete,
    handleUploadProgress,
    reset,
    attachmentFailed,
    send,
  };
};
